import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ToolCard } from '../../types/agent';
import { groupSteps, isCardVisible, type StepGroup } from '../../services/agent/ChatSession';
import { toolLabel } from '../../services/agent/toolCatalogue';
import { qualify } from '../../services/excel/a1';
import { agentLabel } from './agentLabel';
import { Collapse, ERROR_HOLD_MS, HOLD_MS, useDeparting, type Departing, type Phase } from './motion';
import { Spinner } from './Spinner';

const keyOf = (c: ToolCard) => c.tool_call_id;

/** Design 1b "steps" — only what is still going on (product feedback 2026-09-05): running, pending and
 *  awaiting steps. A settled step shows its mark for a moment and collapses away (`useDeparting`); a
 *  failed one does the same, only held longer, because it is nearly always a step the agent is about to
 *  try another way. Sub-agent steps sit under a mono agent row (PLAN §11.5) that leaves with its last
 *  step. */
export function StepsList({ cards, onShow }: { cards: ToolCard[]; onShow?: (c: ToolCard) => void }) {
  const isVisible = useCallback((c: ToolCard) => isCardVisible(c), []);
  // A failure is worth reading before it goes; a success is worth a glance.
  const holdFor = useCallback((c: ToolCard) => (c.status === 'error' ? ERROR_HOLD_MS : HOLD_MS), []);
  const rendered = useDeparting(cards, keyOf, isVisible, holdFor);
  if (!rendered.length) return null;
  const phaseOf = new Map(rendered.map((r) => [r.item.tool_call_id, r.phase]));
  const groups = groupSteps(rendered.map((r) => r.item));
  return (
    <div className="flex flex-col pl-0.5">
      {groups.map((g, i) =>
        g.agent === null ? (
          g.cards.map((c) => <StepRow key={c.tool_call_id} card={c} phase={phaseOf.get(c.tool_call_id) ?? 'present'} onShow={onShow} />)
        ) : (
          <AgentGroup key={`${g.agent}-${i}`} group={g} phases={phaseOf} onShow={onShow} />
        ),
      )}
    </div>
  );
}

function AgentGroup({ group, phases, onShow }: { group: StepGroup; phases: Map<string, Phase>; onShow?: (c: ToolCard) => void }) {
  const { t } = useTranslation();
  const label = agentLabel(group.agent ?? 'main', t);
  const leaving = group.cards.every((c) => phases.get(c.tool_call_id) === 'leaving');
  return (
    <Collapse leaving={leaving}>
      <div className="flex animate-pc-in-enter flex-col">
        <div className="flex h-[22px] items-center gap-2 text-[12px]">
          <GroupDot status={group.status} />
          <span className={`font-mono text-[11px] ${group.status === 'error' ? 'text-warn' : 'text-ink'}`}>{label}</span>
        </div>
        <div className="ml-[5px] flex flex-col border-l border-line-soft pl-3">
          {group.cards.map((c) => (
            <StepRow key={c.tool_call_id} card={c} phase={phases.get(c.tool_call_id) ?? 'present'} onShow={onShow} />
          ))}
        </div>
      </div>
    </Collapse>
  );
}

function StepRow({ card, phase, onShow }: { card: ToolCard; phase: Phase; onShow?: (c: ToolCard) => void }) {
  const target = card.target ? qualify(card.target.sheet, card.target.address) : null;
  const failed = card.status === 'error';
  const settled = phase !== 'present';
  const colour = failed ? 'text-warn' : card.status === 'running' ? 'text-ink' : settled ? 'text-muted' : 'text-fainter';
  const clickable = !!target && !!onShow && failed;
  return (
    <Collapse leaving={phase === 'leaving'}>
      <div className="flex h-[22px] animate-pc-in-enter items-center gap-2 py-px text-[12px] text-muted" data-step-status={card.status} data-phase={phase} title={card.error ?? undefined}>
        <StepDot status={card.status} />
        <span className={`truncate transition-colors duration-200 ${colour}`}>
          {toolLabel(card.tool_name)}
          {failed && card.error ? <span className="text-warn"> — {card.error}</span> : null}
        </span>
        {target && (
          <button type="button" disabled={!clickable} onClick={() => onShow?.(card)} className={`ml-auto shrink-0 font-mono text-[11px] text-faint ${clickable ? 'hover:text-accent' : 'cursor-default'}`}>
            {target}
          </button>
        )}
      </div>
    </Collapse>
  );
}

function GroupDot({ status }: { status: StepGroup['status'] }) {
  return <StepDot status={status === 'active' ? 'running' : status === 'done' ? 'done' : status === 'error' ? 'error' : 'pending'} />;
}

/** Status dot. Settled states (✓ ! ×) pop in (`pc-pop`) — the spinner "morphs" into the mark because
 *  the dot is keyed on its status, so the mark mounts fresh when the step finishes. */
export function StepDot({ status }: { status: ToolCard['status'] }) {
  switch (status) {
    case 'done':
      return (
        <div key="done" className="grid h-3 w-3 shrink-0 animate-pc-pop place-items-center rounded-full bg-accent text-[8px] leading-none text-white">
          ✓
        </div>
      );
    case 'running':
      return <Spinner />;
    case 'error':
      return (
        <div key="error" className="grid h-3 w-3 shrink-0 animate-pc-pop place-items-center rounded-full bg-warn text-[8px] font-semibold leading-none text-white">
          !
        </div>
      );
    case 'cancelled':
      return (
        <div key="cancelled" className="grid h-3 w-3 shrink-0 animate-pc-pop place-items-center rounded-full border-[1.5px] border-line text-[9px] leading-none text-fainter">
          ×
        </div>
      );
    default:
      return <div className="h-3 w-3 shrink-0 rounded-full border-[1.5px] border-[#d0d2ce]" />;
  }
}

export type { Departing };
